import React, { useEffect, useState } from 'react';
import { Trash2, Check, CalendarClock, Flag, Receipt, Wallet, Pencil } from 'lucide-react';
import { STATUS, fd, pd } from './calendarLogic';
import { StatusBadge } from './StatusBadge';
import { TaskList } from './TaskList';

const StepRow = ({ r, orderId, onToggle, onDate }) => {
  const tone = { done: 'text-emerald-700 bg-emerald-50/60', late: 'text-red-700 bg-red-50/60', now: 'text-amber-800 bg-amber-50/60', next: 'text-slate-600' }[r.state];
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors ${tone}`}>
      <input
        type="checkbox"
        data-testid={`step-checkbox-${orderId}-${r.i}`}
        checked={!!r.dn}
        onChange={(e) => onToggle(r.i, e.target.checked)}
        className="w-4 h-4 accent-emerald-600 cursor-pointer"
      />
      <span className={`text-sm font-medium flex-1 ${r.dn ? 'line-through opacity-70' : ''}`}>{r.n} <span className="text-xs opacity-60">({r.days} {r.days > 1 ? 'días' : 'día'})</span></span>
      {r.dn ? (
        <span className="flex items-center gap-1 text-xs">Listo
          <input type="date" data-testid={`step-date-${orderId}-${r.i}`} value={r.dn} title="Fecha en que se terminó el paso"
            onChange={(e) => validDate(e.target.value) && onDate(r.i, e.target.value)}
            className="px-1.5 py-0.5 font-mono text-xs bg-transparent border border-emerald-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-400" />
        </span>
      ) : (
        <span className="text-xs font-mono">{`${fd(r.st)}${r.days > 1 ? ` → ${fd(r.en)}` : ''}`}</span>
      )}
    </div>
  );
};

const validDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && +v.slice(0, 4) >= 2000;

const DateField = ({ icon: Icon, label, value, onChange, testId }) => {
  const [local, setLocal] = useState(value || '');
  useEffect(() => { setLocal(value || ''); }, [value]);
  const change = (v) => { setLocal(v); if (v === '' || validDate(v)) onChange(v); };
  return (
    <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      <span className="flex items-center gap-1"><Icon size={12} /> {label}</span>
      <input type="date" lang="es-CO" data-testid={testId} value={local} onChange={(e) => change(e.target.value)}
        className="px-2 py-1.5 text-sm font-normal normal-case tracking-normal text-slate-800 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400" />
    </label>
  );
};

export const OrderCard = ({ o, pl, today, onToggle, onUpdate, onDelete, onEdit }) => {
  const [sure, setSure] = useState(false);
  const s = STATUS[pl.status];
  const pct = Math.round((100 * pl.done) / pl.rows.length);
  const del = () => { if (sure) onDelete(o.id); else { setSure(true); setTimeout(() => setSure(false), 3500); } };
  return (
    <div data-testid={`pedido-card-${o.id}`} className={`bg-white border border-slate-200 border-l-4 ${s.ring} rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow animate-in fade-in slide-in-from-bottom-2`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{o.cliente}</h3>
          <p className="text-sm text-slate-500">{pl.type.n} · {o.cantidad} {pl.type.u}</p>
        </div>
        <StatusBadge pl={pl} testId={`margin-badge-${o.id}`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <DateField icon={Receipt} label={o.fechaFactura ? 'Facturación ✓' : 'Facturación (pendiente)'} value={o.fechaFactura} onChange={(v) => onUpdate(o.id, { fechaFactura: v })} testId={`fecha-factura-${o.id}`} />
        <DateField icon={Wallet} label="Primer abono" value={o.fechaAbono} onChange={(v) => v && onUpdate(o.id, { fechaAbono: v })} testId={`fecha-abono-${o.id}`} />
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500"><span className="flex items-center gap-1"><CalendarClock size={12} /> Proyección</span><div className="mt-2 text-sm font-mono normal-case text-slate-800">{fd(pl.fc)}</div></div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500"><span className="flex items-center gap-1"><Flag size={12} /> Límite</span><div className="mt-2 text-sm font-mono normal-case text-slate-800">{fd(pl.dead)}</div></div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${s.bar} rounded-full transition-[width] duration-500`} style={{ width: `${pct}%` }} /></div>
        <span className="text-xs font-mono font-bold text-slate-600">{pl.done}/{pl.rows.length}</span>
      </div>
      <div className="mt-3 space-y-0.5">{pl.rows.map((r) => <StepRow key={r.i} r={r} orderId={o.id} onToggle={(i, v) => onToggle(o.id, i, v)} onDate={(i, d) => onUpdate(o.id, { done: { ...o.done, [i]: d } })} />)}</div>
      {o.notas ? <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{o.notas}</p> : null}
      <TaskList o={o} today={today} onChange={(tareas) => onUpdate(o.id, { tareas })} />
      <div className="mt-4 flex justify-between items-center">
        {pl.finished ? <span className="text-sm font-semibold text-blue-700 flex items-center gap-1"><Check size={16} /> Pedido terminado</span> : <span />}
        <div className="flex gap-1">
        <button data-testid={`btn-edit-pedido-${o.id}`} onClick={() => onEdit(o)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"><Pencil size={14} /> Editar</button>
        <button data-testid={`btn-delete-pedido-${o.id}`} onClick={del}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${sure ? 'bg-red-600 text-white hover:bg-red-700' : 'text-red-600 hover:bg-red-50'}`}>
          <Trash2 size={14} /> {sure ? '¿Seguro? Toca otra vez' : 'Eliminar'}
        </button>
        </div>
      </div>
    </div>
  );
};
